'use client'

import type { ReactNode } from 'react'
import { ChevronDown, RefreshCw } from 'lucide-react'

import type { RenderLink } from '@ui/web/utils'
import { Button, Skeleton } from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import { getStaticAssetUrl } from '@helper/utils/file'
import cn from '@config/tailwind/cn'

const BOOK_GRID =
  'srd:grid srd:grid-cols-2 srd:sm:grid-cols-3 srd:gap-4 srd:md:grid-cols-4 srd:lg:grid-cols-5 srd:xl:grid-cols-6 srd:2xl:grid-cols-8'
const WIDE_GRID =
  'srd:grid srd:grid-cols-1 srd:sm:grid-cols-2 srd:gap-4 srd:md:grid-cols-3 srd:lg:grid-cols-4 srd:xl:grid-cols-5 srd:2xl:grid-cols-6'

type SkeletonVariant = 'book' | 'wide'

interface ResultGroupProps<T> {
  isPending: boolean
  fetchStatus: 'fetching' | 'paused' | 'idle'
  isError: boolean
  isFetchingNextPage: boolean
  hasNextPage: boolean
  items: T[]
  variant: SkeletonVariant
  emptyKeyword?: string
  renderExplorePage: RenderLink
  renderSearchPage: RenderLink
  onRetry: () => void
  onLoadMore: () => void
  children: ReactNode
}

export function ResultGroup<T>({
  isPending,
  fetchStatus,
  isError,
  isFetchingNextPage,
  hasNextPage,
  items,
  variant,
  emptyKeyword,
  renderExplorePage,
  renderSearchPage,
  onRetry,
  onLoadMore,
  children,
}: ResultGroupProps<T>) {
  const isActuallyLoading = isPending && fetchStatus !== 'idle'

  if (isActuallyLoading) {
    return (
      <div className={variant === 'book' ? BOOK_GRID : WIDE_GRID}>
        {Array.from({ length: 12 }).map((_, index) => (
          <Skeleton
            key={`favorite-skeleton-${index}`}
            className={cn('srd:w-full srd:rounded-md', variant === 'book' ? 'srd:aspect-3/4' : 'srd:aspect-4/3')}
          />
        ))}
      </div>
    )
  }

  if (isError) {
    return (
      <div className='srd:flex srd:flex-col srd:items-center srd:justify-center srd:gap-y-4 srd:min-h-[40dvh] srd:text-center srd:py-10 srd:text-neutral-white'>
        <p className={typographyVariants({ variant: 'title-md' })}>Oops, Terjadi Kesalahan</p>
        <p className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:opacity-90')}>
          Gagal memuat data. Silakan coba lagi.
        </p>
        <Button onClick={onRetry} variant='secondary'>
          <RefreshCw className='srd:size-4 srd:mr-2' />
          Coba Lagi
        </Button>
      </div>
    )
  }

  if (items.length === 0) {
    if (emptyKeyword) {
      return (
        <div className='srd:flex srd:flex-col srd:items-center srd:justify-center srd:min-h-[40dvh] srd:text-center srd:py-10 srd:text-neutral-white'>
          <p className={cn(typographyVariants({ variant: 'body-md' }), 'srd:opacity-90')}>
            Tidak ada hasil untuk "{emptyKeyword}".
          </p>
        </div>
      )
    }

    return (
      <div className='srd:flex srd:flex-col srd:items-center srd:justify-center srd:min-h-[40dvh] srd:text-center srd:py-10 srd:text-neutral-white srd:gap-y-6 srd:max-w-[800px] srd:mx-auto'>
        <h6 className={cn(typographyVariants({ variant: 'title-md' }))}>Belum ada yang disimpan</h6>

        <img
          src={getStaticAssetUrl('illustration/foam.png')}
          alt=''
          draggable={false}
          className='srd:w-auto srd:h-auto srd:max-h-32'
        />

        <p className={cn(typographyVariants({ variant: 'body-lg' }))}>
          Ada buku atau pelajaran yang kamu sukai? Simpan di perpustakaan pribadimu di sini agar mudah kalau kamu ingin
          lihat lagi.
        </p>

        <div className='srd:flex srd:flex-col srd:items-center srd:justify-center srd:gap-y-2'>
          <Button asChild>{renderExplorePage({ children: 'Jelajahi beranda' })}</Button>
          <Button asChild variant='outline' className='srd:text-neutral-white'>
            {renderSearchPage({ children: 'Cari berdasarkan kategori' })}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <section className='srd:flex srd:flex-col srd:gap-y-6'>
      {children}
      {hasNextPage && (
        <div className='srd:flex srd:justify-center'>
          <Button
            type='button'
            variant='secondary'
            loading={isFetchingNextPage}
            disabled={isFetchingNextPage}
            onClick={onLoadMore}
          >
            Muat lebih banyak <ChevronDown className='srd:size-4 srd:ml-2' />
          </Button>
        </div>
      )}
    </section>
  )
}

export { BOOK_GRID, WIDE_GRID }
